import React, { createContext, useContext, useEffect, useState } from 'react';
import { validateEnvironmentVariables, secureLog } from '../utils/security';

const SecurityContext = createContext();

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurityContext must be used within a SecurityProvider');
  }
  return context;
};

export const SecurityProvider = ({ children }) => {
  const [isSecure, setIsSecure] = useState(false);
  const [securityChecks, setSecurityChecks] = useState({
    environmentVariables: false,
    httpsEnabled: false,
    browserSecurity: false
  });

  useEffect(() => {
    const performSecurityChecks = () => {
      const checks = {
        // Check environment variables
        environmentVariables: validateEnvironmentVariables(),
        
        // Check if HTTPS is enabled (in production)
        httpsEnabled: process.env.NODE_ENV === 'development' || 
                     window.location.protocol === 'https:',
        
        // Check basic browser security features
        browserSecurity: typeof window !== 'undefined' && 
                        'crypto' in window && 
                        'localStorage' in window
      };

      setSecurityChecks(checks);
      
      const allSecure = Object.values(checks).every(check => check);
      setIsSecure(allSecure);

      if (!allSecure) {
        secureLog('Security checks failed', checks);
      }

      // Additional runtime security measures
      if (typeof window !== 'undefined') {
        // Disable right-click context menu in production (optional)
        if (process.env.NODE_ENV === 'production') {
          document.addEventListener('contextmenu', (e) => {
            // Allow context menu for development/debugging
            // Uncomment the line below to disable right-click in production
            // e.preventDefault();
          });
        }

        // Detect developer tools (basic detection)
        let devtools = { open: false };
        const threshold = 160;
        
        const detectDevTools = () => {
          if (window.outerHeight - window.innerHeight > threshold || 
              window.outerWidth - window.innerWidth > threshold) {
            if (!devtools.open) {
              devtools.open = true;
              secureLog('Developer tools detected');
            }
          } else {
            devtools.open = false;
          }
        };

        // Check every 500ms
        const devToolsInterval = setInterval(detectDevTools, 500);
        
        return () => clearInterval(devToolsInterval);
      }
    };

    performSecurityChecks();
  }, []);

  const value = {
    isSecure,
    securityChecks,
    performSecurityCheck: () => {
      // Re-run security checks if needed
      return validateEnvironmentVariables();
    }
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};
